/* ==========================================================================
   WAVES ALCHEMY — Lab upgrade layer
   Loaded after theme.js. Additive: every feature here is progressive
   enhancement over markup that already works without it.

   1. Waveform preview player
   2. Sticky buy bar
   3. Quick add / bundle add
   4. Cart threshold progress
   5. Motion layer (magnetic CTA, spotlight cards, line reveal, parallax)
   ========================================================================== */
(function () {
  'use strict';

  var strings = window.themeStrings || {};
  var routes = window.themeRoutes || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia('(hover: none)').matches;

  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }

  function toast(msg) {
    var el = $('#toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-open');
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('is-open'); }, 3200);
  }

  function money(cents) {
    var format = strings.moneyFormat || '{{amount}}';
    var value = cents / 100;
    function delimit(num, precision, thousands, decimal) {
      var parts = (num || 0).toFixed(precision).split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return intPart + (parts[1] ? decimal + parts[1] : '');
    }
    if (/amount_no_decimals_with_comma_separator/.test(format)) return format.replace(/\{\{\s*\w+\s*\}\}/, delimit(value, 0, '.', ','));
    if (/amount_with_comma_separator/.test(format)) return format.replace(/\{\{\s*\w+\s*\}\}/, delimit(value, 2, '.', ','));
    if (/amount_no_decimals/.test(format)) return format.replace(/\{\{\s*\w+\s*\}\}/, delimit(value, 0, ',', '.'));
    return format.replace(/\{\{\s*\w+\s*\}\}/, delimit(value, 2, ',', '.'));
  }

  function clock(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '--:--';
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* ======================================================================
     0. BUNDLE SHUFFLE
     ======================================================================
     Liquid has no random, and a Shopify page is cached, so a rotation written
     in the template would show the same pair to everyone until the cache
     expired. Instead the section renders a pool of candidates with the extras
     marked spare, and this picks the companions here, in the browser, on every
     visit.

     It runs before the players are built and before the bundle wires itself
     up, so the kits that lose the draw are gone from the document first: no
     audio player is constructed for them and the totals never count them.
     With JavaScript off, the first few candidates stay visible and the section
     behaves exactly as it did before. */
  (function () {
    $$('[data-bundle]').forEach(function (root) {
      var wrap = $('[data-bundle-pool]', root);
      if (!wrap || root.getAttribute('data-shuffle') !== 'true') return;

      var want = parseInt(root.getAttribute('data-shuffle-keep'), 10) || 0;
      var pool = $$('[data-bundle-candidate]', wrap);
      if (!want || pool.length <= want) {
        pool.forEach(function (el) { el.hidden = false; });
        return;
      }

      /* Fisher–Yates. Partial is enough: only the first `want` slots matter. */
      for (var i = 0; i < want; i++) {
        var j = i + Math.floor(Math.random() * (pool.length - i));
        var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
      }

      pool.forEach(function (el, idx) {
        if (idx < want) {
          el.hidden = false;
          el.removeAttribute('data-bundle-candidate');
          wrap.appendChild(el);
        } else if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    });
  })();

  /* ======================================================================
     1. WAVEFORM PREVIEW PLAYER
     ====================================================================== */

  /* Mulberry32 — a small deterministic PRNG. The placeholder waveform has to
     be the same shape on every reload, or the page looks like it is rendering
     different audio each visit. Seeding from the product id gives every kit
     its own stable silhouette. */
  function seededRandom(seed) {
    var a = (seed >>> 0) || 1;
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* A believable placeholder: a slow envelope (so it breathes like a musical
     phrase) modulated by seeded noise, rather than uniform random spikes. */
  function placeholderPeaks(seed, count) {
    var rnd = seededRandom(seed);
    var out = [];
    var drift = rnd();
    for (var i = 0; i < count; i++) {
      var t = i / count;
      var envelope = 0.42 + 0.34 * Math.sin(t * Math.PI * 2.4 + drift * 6.283) + 0.16 * Math.sin(t * Math.PI * 7.1 + drift * 3.1);
      var v = envelope * (0.62 + rnd() * 0.5);
      out.push(Math.max(0.08, Math.min(1, v)));
    }
    return out;
  }

  /* RMS per bucket, not peak — peak amplitude makes every waveform look like a
     solid block because a single transient pins the bucket to 1.0. RMS keeps
     the dynamics readable. */
  function peaksFromBuffer(buffer, count) {
    var channel = buffer.getChannelData(0);
    var block = Math.floor(channel.length / count) || 1;
    var out = [];
    var max = 0;
    for (var i = 0; i < count; i++) {
      var sum = 0;
      var start = i * block;
      var end = Math.min(start + block, channel.length);
      for (var j = start; j < end; j++) sum += channel[j] * channel[j];
      var rms = Math.sqrt(sum / Math.max(1, end - start));
      out.push(rms);
      if (rms > max) max = rms;
    }
    if (max <= 0) return placeholderPeaks(1, count);
    // Normalise, then a mild curve so quiet passages stay visible.
    return out.map(function (v) { return Math.max(0.06, Math.pow(v / max, 0.72)); });
  }

  var audioCtx = null;
  function getCtx() {
    if (audioCtx) return audioCtx;
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    try { audioCtx = new Ctx(); } catch (e) { audioCtx = null; }
    return audioCtx;
  }

  /* One decode per URL, shared by every player pointing at the same file
     (product page + sticky bar + card all reference the same preview). */
  var peakCache = {};

  function decodePeaks(url, count) {
    var key = url + '|' + count;
    if (peakCache[key]) return peakCache[key];
    var ctx = getCtx();
    if (!ctx || !window.fetch) return Promise.reject();
    peakCache[key] = fetch(url, { mode: 'cors' })
      .then(function (r) {
        if (!r.ok) throw new Error('http');
        return r.arrayBuffer();
      })
      .then(function (buf) {
        return new Promise(function (resolve, reject) {
          // Callback form for Safari, which shipped decodeAudioData before it
          // returned a promise.
          var p = ctx.decodeAudioData(buf, resolve, reject);
          if (p && p.then) p.then(resolve, reject);
        });
      })
      .then(function (audioBuffer) { return peaksFromBuffer(audioBuffer, count); });
    return peakCache[key];
  }

  var activePlayer = null;

  function buildPlayer(root) {
    var audio = $('[data-wf-audio]', root);
    var toggle = $('[data-wf-toggle]', root);
    var scrub = $('[data-wf-scrub]', root);
    var track = $('[data-wf-track]', root);
    var trackFill = $('[data-wf-track-fill]', root);
    var fill = $('[data-wf-fill]', root);
    var head = $('[data-wf-head]', root);
    var curEl = $('[data-wf-current]', root);
    var durEl = $('[data-wf-duration]', root);
    var titleEl = $('[data-wf-title]', root);
    var tabsEl = $('[data-wf-tabs]', root);
    if (!audio || !toggle || !track) return;

    var count = parseInt(root.getAttribute('data-wf-bars'), 10) || 48;

    var seed = parseInt(root.getAttribute('data-wf-seed'), 10) || 7;

    var tracks = [];
    try { tracks = JSON.parse(root.getAttribute('data-wf-tracks') || '[]'); } catch (e) { tracks = []; }
    tracks = tracks.filter(function (t) { return t && t.u; });
    if (!tracks.length) { root.hidden = true; return; }

    var barsA = [];
    var barsB = [];
    for (var i = 0; i < count; i++) {
      var a = document.createElement('i');
      var b = document.createElement('i');
      track.appendChild(a); barsA.push(a);
      trackFill.appendChild(b); barsB.push(b);
    }

    function paint(peaks, animate) {
      for (var i = 0; i < count; i++) {
        var h = (peaks[i] * 100).toFixed(1) + '%';
        if (animate) {
          barsA[i].style.transitionDelay = (i * 6) + 'ms';
          barsB[i].style.transitionDelay = (i * 6) + 'ms';
        }
        barsA[i].style.height = h;
        barsB[i].style.height = h;
      }
      if (animate) {
        setTimeout(function () {
          barsA.forEach(function (el) { el.style.transitionDelay = ''; });
          barsB.forEach(function (el) { el.style.transitionDelay = ''; });
        }, count * 6 + 400);
      }
    }

    /* Baked peaks only make sense for a single-demo pack — one array cannot
       describe four different files. */
    var given = tracks.length === 1 ? root.getAttribute('data-wf-peaks') : null;
    var bakedPeaks = null;
    if (given) {
      try {
        var parsed = JSON.parse(given);
        if (parsed && parsed.length) {
          var step = parsed.length / count;
          bakedPeaks = [];
          for (var k = 0; k < count; k++) bakedPeaks.push(Math.max(0.06, Math.min(1, parsed[Math.floor(k * step)])));
        }
      } catch (e) { bakedPeaks = null; }
    }

    function setProgress(ratio) {
      var pct = Math.max(0, Math.min(1, ratio)) * 100;
      fill.style.width = pct + '%';
      if (head) head.style.left = pct + '%';
      scrub.setAttribute('aria-valuenow', Math.round(pct));
      scrub.setAttribute('aria-valuetext', clock(audio.currentTime) + (isFinite(audio.duration) ? ' / ' + clock(audio.duration) : ''));
    }

    /* The fill layer is a clipping window over a second copy of the bars, and
       that copy must stay the width of the whole scrubber or the bars would
       squash as playback advances instead of being revealed. */
    function sizeFill() { trackFill.style.width = scrub.offsetWidth + 'px'; }
    sizeFill();
    if ('ResizeObserver' in window) new ResizeObserver(sizeFill).observe(scrub);
    else window.addEventListener('resize', sizeFill);

    /* ---- track switching ---- */
    var index = -1;
    var tabs = [];

    /* A kit with a dozen one-shot demos overflows the pill row several times
       over, and the row hides its scrollbar — so without these two the extra
       demos are not merely off-screen, they are unannounced. `fadeTabs` masks
       whichever edge still has demos behind it, which is the only signal that
       the row scrolls at all; `revealTab` keeps the current pill on screen when
       the selection moves on its own (a demo ending and rolling into the next)
       or from a tap, neither of which moves focus and neither of which the
       browser scrolls for us. */
    function fadeTabs() {
      if (!tabsEl) return;
      var over = tabsEl.scrollWidth - tabsEl.clientWidth;
      var x = tabsEl.scrollLeft;
      tabsEl.style.setProperty('--fade-l', over > 1 && x > 1 ? '26px' : '0px');
      tabsEl.style.setProperty('--fade-r', over > 1 && x < over - 1 ? '26px' : '0px');
    }

    function revealTab(i) {
      var tab = tabs[i];
      if (!tab || !tabsEl || tabsEl.scrollWidth <= tabsEl.clientWidth) return;
      /* Measured from rects, not offsetLeft: the row is not a positioned
         element, so offsetLeft is relative to whatever ancestor happens to be
         positioned and carries the page's own centring offset with it. */
      var tabBox = tab.getBoundingClientRect();
      var rowBox = tabsEl.getBoundingClientRect();
      var target = tabsEl.scrollLeft + (tabBox.left - rowBox.left) - (rowBox.width - tabBox.width) / 2;
      var max = tabsEl.scrollWidth - tabsEl.clientWidth;
      target = Math.max(0, Math.min(max, target));
      if (tabsEl.scrollTo) {
        tabsEl.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        tabsEl.scrollLeft = target;
      }
    }

    function load(i, autoplay) {
      if (i === index || !tracks[i]) return;
      index = i;
      var t = tracks[i];

      audio.src = t.u;
      if (titleEl) titleEl.textContent = t.t || ('Demo ' + (i + 1));
      if (durEl) durEl.textContent = '--:--';
      if (curEl) curEl.textContent = '0:00';
      setProgress(0);
      root.classList.remove('is-analysed', 'is-error');

      tabs.forEach(function (tab, n) {
        var on = n === i;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      revealTab(i);

      // Show something immediately: baked peaks, a cached decode, or the
      // per-track placeholder. Never an empty box.
      if (bakedPeaks) paint(bakedPeaks, false);
      else paint(placeholderPeaks(seed + i * 977, count), false);

      var cached = peakCache[t.u + '|' + count];
      if (cached) cached.then(function (peaks) {
        if (index === i) { root.classList.add('is-analysed'); paint(peaks, false); }
      }).catch(function () {});

      if (autoplay) {
        root.classList.add('is-loading');
        audio.play().catch(function () {
          root.classList.remove('is-loading');
          toast(strings.previewError || 'Preview unavailable');
        });
      }
    }

    if (tracks.length > 1 && tabsEl) {
      tabsEl.hidden = false;
      tracks.forEach(function (t, i) {
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'wf__tab';
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
        tab.textContent = t.t || ('Demo ' + (i + 1));
        tab.addEventListener('click', function () { load(i, true); });
        tabsEl.appendChild(tab);
        tabs.push(tab);
      });

      // Arrow keys walk the demo list, which is what a tablist is expected to
      // do and saves tabbing through every demo to reach the waveform.
      tabsEl.addEventListener('keydown', function (e) {
        var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var next = (index + dir + tabs.length) % tabs.length;
        load(next, false);
        tabs[next].focus();
      });

      tabsEl.addEventListener('scroll', fadeTabs, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(fadeTabs).observe(tabsEl);
      else window.addEventListener('resize', fadeTabs);
      fadeTabs();
    }

    load(0, false);

    /* ---- decode the real waveform for the current track ---- */
    var decoding = {};
    function ensureRealPeaks() {
      var i = index;
      var url = tracks[i] && tracks[i].u;
      if (!url || bakedPeaks || decoding[url]) return;
      decoding[url] = true;
      decodePeaks(url, count).then(function (peaks) {
        if (index !== i) return; // the visitor switched demo mid-decode
        root.classList.add('is-analysed');
        paint(peaks, !reduceMotion);
      }).catch(function () { /* placeholder stands — nothing to tell the user */ });
    }

    audio.addEventListener('loadedmetadata', function () {
      if (durEl) durEl.textContent = clock(audio.duration);
      sizeFill();
    });

    audio.addEventListener('timeupdate', function () {
      if (!audio.duration) return;
      setProgress(audio.currentTime / audio.duration);
      if (curEl) curEl.textContent = clock(audio.currentTime);
    });

    audio.addEventListener('play', function () {
      if (activePlayer && activePlayer !== root && activePlayer._stop) activePlayer._stop();
      activePlayer = root;
      root.classList.add('is-playing');
      root.classList.remove('is-loading');
      toggle.setAttribute('aria-label', strings.pausePreview || 'Pause preview');
      ensureRealPeaks();
    });

    function stopped() {
      root.classList.remove('is-playing', 'is-loading');
      toggle.setAttribute('aria-label', strings.playPreview || 'Play preview');
      if (activePlayer === root) activePlayer = null;
    }
    audio.addEventListener('pause', stopped);
    audio.addEventListener('ended', function () {
      stopped();
      // Roll into the next demo rather than stopping dead — on a multi-demo
      // pack that is what someone auditioning the kit wants next.
      if (tracks.length > 1 && index < tracks.length - 1) load(index + 1, true);
      else { setProgress(0); if (curEl) curEl.textContent = '0:00'; }
    });
    audio.addEventListener('waiting', function () { root.classList.add('is-loading'); });
    audio.addEventListener('playing', function () { root.classList.remove('is-loading'); });
    audio.addEventListener('error', function () { root.classList.add('is-error'); stopped(); });

    root._stop = function () { audio.pause(); };

    toggle.addEventListener('click', function () {
      if (audio.paused) {
        root.classList.add('is-loading');
        audio.play().catch(function () {
          root.classList.remove('is-loading');
          toast(strings.previewError || 'Preview unavailable');
        });
      } else {
        audio.pause();
      }
    });

    /* ---- scrubbing ---- */
    function seekFromEvent(e) {
      var rect = scrub.getBoundingClientRect();
      var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      var ratio = Math.max(0, Math.min(1, x / rect.width));
      if (audio.duration) audio.currentTime = ratio * audio.duration;
      setProgress(ratio);
    }

    var dragging = false;
    scrub.addEventListener('pointerdown', function (e) {
      dragging = true;
      if (scrub.setPointerCapture) scrub.setPointerCapture(e.pointerId);
      seekFromEvent(e);
    });
    scrub.addEventListener('pointermove', function (e) { if (dragging) seekFromEvent(e); });
    scrub.addEventListener('pointerup', function () { dragging = false; });
    scrub.addEventListener('pointercancel', function () { dragging = false; });

    scrub.addEventListener('keydown', function (e) {
      var d = audio.duration || 0;
      var handled = true;
      switch (e.key) {
        case 'ArrowRight': case 'ArrowUp': audio.currentTime = Math.min(d, audio.currentTime + 5); break;
        case 'ArrowLeft': case 'ArrowDown': audio.currentTime = Math.max(0, audio.currentTime - 5); break;
        case 'Home': audio.currentTime = 0; break;
        case 'End': audio.currentTime = d; break;
        case ' ': case 'Enter': toggle.click(); break;
        default: handled = false;
      }
      if (handled) { e.preventDefault(); if (d) setProgress(audio.currentTime / d); }
    });

    /* Hovering the waveform shows where a click would land — the affordance
       every DAW gives, so producers read it without being told. */
    if (!coarse) {
      scrub.addEventListener('pointermove', function (e) {
        var rect = scrub.getBoundingClientRect();
        scrub.style.setProperty('--wf-hover', (((e.clientX - rect.left) / rect.width) * 100).toFixed(2) + '%');
      });
    }
  }

  $$('[data-wf]').forEach(buildPlayer);

  /* Anything that plays anywhere silences everything else, including the old
     card players from theme.js. */
  document.addEventListener('play', function (e) {
    if (e.target.tagName !== 'AUDIO') return;
    $$('audio').forEach(function (a) { if (a !== e.target && !a.paused) a.pause(); });
  }, true);

  /* ======================================================================
     2. STICKY BUY BAR
     ====================================================================== */
  (function () {
    var bar = $('[data-buy-bar]');
    var anchor = $('[data-buy-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    /* Shown once the real add-to-cart button has scrolled out of view, hidden
       again when it comes back — never both on screen at once, which would
       read as two competing CTAs. */
    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-on', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
    }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' }).observe(anchor);

    var proxy = $('[data-buy-bar-add]', bar);
    var realForm = $('[data-product-form]');
    if (proxy && realForm) {
      proxy.addEventListener('click', function () {
        var real = $('[data-add-to-cart]', realForm);
        if (real) real.click();
      });
    }

    /* The variant picker in theme.js only knows about the price inside
       [data-product-root]. Mirroring it keeps the bar from quoting a price the
       visitor is no longer looking at, without either file reaching into the
       other's logic. */
    var root = $('[data-product-root]');
    var srcPrice = root && $('[data-product-price]', root);
    var srcCompare = root && $('[data-product-compare]', root);
    var dstPrice = $('[data-product-price]', bar);
    var dstCompare = $('[data-product-compare]', bar);
    if (srcPrice && dstPrice && 'MutationObserver' in window) {
      var mirror = function () {
        dstPrice.textContent = srcPrice.textContent;
        dstPrice.className = srcPrice.className;
        if (srcCompare && dstCompare) {
          dstCompare.textContent = srcCompare.textContent;
          dstCompare.hidden = srcCompare.hidden;
        }
        var real = $('[data-add-to-cart]', realForm || document);
        if (real) {
          proxy.disabled = real.disabled;
          proxy.textContent = real.textContent.trim();
        }
      };
      new MutationObserver(mirror).observe(root, {
        subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'disabled', 'class']
      });
    }
  })();

  /* ======================================================================
     3. QUICK ADD  +  BUNDLE ADD
     ====================================================================== */
  function cartAdd(items) {
    /* routes.cartAdd is the plain form endpoint ("/cart/add"). The AJAX API
       lives at the same path with a .js suffix — posting JSON to the bare path
       gets a redirect back, not a cart object. */
    return fetch((routes.cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || d.message || 'Error'); });
      return r.json();
    });
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('wa:cart:refresh'));
    var opener = $('[data-panel-open="cart-drawer"]');
    if (opener) opener.click();
  }

  function busy(btn, on, doneLabel) {
    if (!btn) return;
    if (on) {
      btn._label = btn.textContent;
      btn.disabled = true;
      btn.classList.add('is-busy');
    } else {
      btn.disabled = false;
      btn.classList.remove('is-busy');
      if (doneLabel) {
        btn.textContent = doneLabel;
        btn.classList.add('is-done');
        setTimeout(function () {
          btn.textContent = btn._label;
          btn.classList.remove('is-done');
        }, 1800);
      } else if (btn._label) {
        btn.textContent = btn._label;
      }
    }
  }

  /* Delegated, so quick-add works on cards the drawer or a section re-renders
     after load as well as the ones present at first paint. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var id = btn.getAttribute('data-quick-add');
    if (!id || !window.fetch) return;
    busy(btn, true);
    cartAdd([{ id: Number(id), quantity: 1 }])
      .then(function () { busy(btn, false, strings.added || 'Added'); refreshCart(); })
      .catch(function (err) { busy(btn, false); toast(err.message || strings.error || 'Something went wrong'); });
  });

  /* The bundle button adds every selected pack in a single request, so the
     drawer opens once with the whole set rather than flashing per item. */
  $$('[data-bundle]').forEach(function (root) {
    var btn = $('[data-bundle-add]', root);
    var totalEl = $('[data-bundle-total]', root);
    var wasEl = $('[data-bundle-was]', root);
    var saveEl = $('[data-bundle-save]', root);
    var countEl = $('[data-bundle-count]', root);
    var boxes = $$('[data-bundle-item]', root);
    var nudgeEl = $('[data-bundle-nudge]', root);
    if (!btn || !boxes.length) return;

    /* The percentage is a mirror of a Shopify automatic discount, never a
       promise this script invents. Checkout is what applies it, so the only
       job here is to show the same arithmetic the customer is about to see. */
    var tierPct = parseFloat(root.getAttribute('data-tier-pct')) || 0;
    var tierMin = parseInt(root.getAttribute('data-tier-min'), 10) || 0;
    var tierNudge = root.getAttribute('data-tier-nudge') || '';
    var tierHit = root.getAttribute('data-tier-hit') || '';

    function recalc() {
      var gross = 0, was = 0, n = 0, paid = 0;
      boxes.forEach(function (box) {
        var on = box.getAttribute('aria-pressed') === 'true';
        box.closest('.bundle__item').classList.toggle('is-off', !on);
        if (!on) return;
        var price = parseInt(box.getAttribute('data-price'), 10) || 0;
        n++;
        if (price > 0) paid++;
        gross += price;
        was += parseInt(box.getAttribute('data-compare'), 10) || price;
      });

      /* Only paid kits count towards the tier, because the Shopify discount is
         scoped to paying collections and would not count a free one either. */
      var earned = tierPct > 0 && tierMin > 0 && paid >= tierMin;
      var total = earned ? gross - Math.round(gross * tierPct / 100) : gross;

      /* One struck-through figure, not two: whichever of the compare-at sum
         and the undiscounted subtotal is higher is the honest "before". */
      var before = was > gross ? was : gross;

      if (totalEl) totalEl.textContent = money(total);
      if (wasEl) { wasEl.textContent = money(before); wasEl.hidden = before <= total; }
      if (saveEl) {
        var save = before - total;
        saveEl.textContent = money(save);
        saveEl.parentElement.hidden = save <= 0;
      }

      if (nudgeEl) {
        if (tierPct > 0 && tierMin > 0) {
          nudgeEl.textContent = earned
            ? tierHit.replace('{{pct}}', tierPct)
            : tierNudge.replace('{{count}}', tierMin - paid).replace('{{pct}}', tierPct);
          nudgeEl.classList.toggle('is-earned', earned);
          nudgeEl.hidden = false;
        } else {
          nudgeEl.hidden = true;
        }
      }

      if (countEl) countEl.textContent = n;
      btn.disabled = n === 0;
    }

    boxes.forEach(function (box) {
      box.addEventListener('click', function () {
        box.setAttribute('aria-pressed', box.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        recalc();
      });
    });
    recalc();

    btn.addEventListener('click', function () {
      var items = boxes
        .filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })
        .map(function (b) { return { id: Number(b.getAttribute('data-variant')), quantity: 1 }; })
        .filter(function (i) { return i.id; });
      if (!items.length || !window.fetch) return;
      busy(btn, true);
      cartAdd(items)
        .then(function () { busy(btn, false, strings.added || 'Added'); refreshCart(); })
        .catch(function (err) { busy(btn, false); toast(err.message || strings.error || 'Something went wrong'); });
    });
  });

  /* ======================================================================
     4. CART: THRESHOLD METER + CROSS-SELL HYGIENE
     ====================================================================== */
  (function () {
    var meter = $('[data-cart-meter]');
    var tier = $('[data-cart-tier]');
    var cross = $('[data-cart-cross]');
    var items = $('[data-cart-items]');
    if (!meter && !tier && !cross) return;

    /* The kit counter towards the bundle discount. Only paid kits count, which
       is what the Shopify discount does too — a free kit in the cart must not
       make this bar promise a discount the checkout then refuses. */
    var tierMin = tier ? (parseInt(tier.getAttribute('data-tier-min'), 10) || 0) : 0;
    var tierPct = tier ? (parseInt(tier.getAttribute('data-tier-pct'), 10) || 0) : 0;
    var tierBelow = tier ? (tier.getAttribute('data-below') || '') : '';
    var tierReached = tier ? (tier.getAttribute('data-reached') || '') : '';
    var tierBar = tier && $('[data-cart-tier-bar]', tier);
    var tierText = tier && $('[data-cart-tier-text]', tier);

    function renderTier(cart) {
      if (!tier || tierMin <= 0 || tierPct <= 0) return;
      var paid = 0;
      (cart.items || []).forEach(function (item) {
        var unit = item.original_price != null ? item.original_price : item.price;
        if (unit > 0) paid += item.quantity;
      });

      tier.hidden = paid === 0;
      var reached = paid >= tierMin;
      var pct = Math.max(0, Math.min(100, (paid / tierMin) * 100));
      if (tierBar) tierBar.style.width = pct + '%';
      var track = $('.cart-meter__track', tier);
      if (track) track.setAttribute('aria-valuenow', Math.round(pct));
      tier.classList.toggle('is-reached', reached);
      if (tierText) {
        tierText.innerHTML = reached
          ? tierReached.replace('{{pct}}', tierPct)
          : tierBelow.replace('{{count}}', '<b>' + (tierMin - paid) + '</b>').replace('{{pct}}', tierPct);
      }
    }

    var goal = meter ? (parseInt(meter.getAttribute('data-goal'), 10) || 0) : 0;
    var barEl = meter && $('[data-cart-meter-bar]', meter);
    var textEl = meter && $('[data-cart-meter-text]', meter);
    var reachedText = meter ? (meter.getAttribute('data-reached') || '') : '';
    var remainingText = meter ? (meter.getAttribute('data-remaining') || '{{amount}}') : '';

    function renderMeter(total) {
      if (!meter || goal <= 0) return;
      var pct = Math.max(0, Math.min(100, (total / goal) * 100));
      if (barEl) barEl.style.width = pct + '%';
      var track = $('.cart-meter__track', meter);
      if (track) track.setAttribute('aria-valuenow', Math.round(pct));
      meter.classList.toggle('is-reached', total >= goal);
      meter.hidden = total === 0;
      if (textEl) {
        textEl.innerHTML = total >= goal
          ? reachedText
          : remainingText.replace('{{amount}}', '<b>' + money(goal - total) + '</b>');
      }
    }

    /* Anything already in the cart is dropped from the suggestions, and the
       whole block disappears once there is nothing left to suggest. */
    /* The drawer holds a pool of candidates and shows a few of them. Which few
       is decided here rather than in the template, and decided again on every
       cart change, because the two rules interact: a kit already in the cart
       must never be suggested, and the suggestions should not be the same
       three kits for everyone forever. Drawing after the cart has been read is
       the only order that satisfies both — shuffling first would sometimes
       pick three kits the visitor already owns and leave the block empty. */
    var crossKeep = cross ? (parseInt(cross.getAttribute('data-cross-keep'), 10) || 3) : 3;

    function renderCross(cart) {
      if (!cross) return;
      var owned = {};
      (cart.items || []).forEach(function (i) { owned[i.product_id] = true; });

      var rows = $$('[data-cross-product]', cross);
      var pool = rows.filter(function (row) {
        return !owned[parseInt(row.getAttribute('data-cross-product'), 10)];
      });

      for (var i = 0; i < crossKeep && i < pool.length; i++) {
        var j = i + Math.floor(Math.random() * (pool.length - i));
        var tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
      }

      rows.forEach(function (row) { row.hidden = true; });
      var shown = pool.slice(0, crossKeep);
      shown.forEach(function (row) { row.hidden = false; });
      cross.hidden = shown.length === 0 || !cart.item_count;
    }

    function sync() {
      if (!window.fetch) return;
      fetch((routes.cart || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderMeter(cart.total_price);
          renderTier(cart);
          renderCross(cart);
        })
        .catch(function () {});
    }

    /* theme.js owns the drawer's line rendering and replaces its innerHTML on
       every change. Watching that node means the meter stays in step with
       quantity edits and removals without either file knowing about the
       other. */
    if (items && 'MutationObserver' in window) {
      var t = null;
      new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(sync, 90);
      }).observe(items, { childList: true });
    }
    document.addEventListener('wa:cart:refresh', function () { setTimeout(sync, 120); });
    sync();
  })();

  /* ======================================================================
     5. MOTION LAYER
     ====================================================================== */
  if (!reduceMotion) {

    /* --- Magnetic primary CTA -------------------------------------------
       Deliberately limited to elements tagged in the markup, and clamped to
       a fraction of the offset so the button never leaves its own hit box.
       Pointer-only: on touch there is no cursor to be magnetic towards. */
    if (!coarse) {
      $$('[data-magnetic]').forEach(function (el) {
        var raf = null, tx = 0, ty = 0;
        function apply() { el.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0)'; raf = null; }
        el.addEventListener('pointermove', function (e) {
          var r = el.getBoundingClientRect();
          tx = (e.clientX - r.left - r.width / 2) * 0.16;
          ty = (e.clientY - r.top - r.height / 2) * 0.28;
          if (!raf) raf = requestAnimationFrame(apply);
        });
        el.addEventListener('pointerleave', function () {
          tx = 0; ty = 0;
          el.style.transform = '';
        });
      });

      /* --- Spotlight cards ---------------------------------------------
         The "magic card" pattern: the cursor position is written to two CSS
         custom properties and the card's own gradient layers do the rest,
         so no per-frame style recalculation of anything but two numbers. */
      var spotlightHosts = $$('[data-spotlight]');
      if (spotlightHosts.length) {
        var pending = null;
        document.addEventListener('pointermove', function (e) {
          if (pending) return;
          pending = requestAnimationFrame(function () {
            pending = null;
            spotlightHosts.forEach(function (card) {
              var r = card.getBoundingClientRect();
              // Skip cards nowhere near the cursor — most of the grid, most of the time.
              if (e.clientX < r.left - 120 || e.clientX > r.right + 120 || e.clientY < r.top - 120 || e.clientY > r.bottom + 120) return;
              card.style.setProperty('--mx', (e.clientX - r.left) + 'px');
              card.style.setProperty('--my', (e.clientY - r.top) + 'px');
            });
          });
        }, { passive: true });
      }
    }

    /* --- Masked word reveal for display headings ------------------------
       Each word is wrapped in an overflow-hidden box and slid up from below,
       so the type appears to rise out of the baseline.

       It walks text nodes rather than rewriting innerHTML, which means the
       serif accent spans, links and any other inline markup inside a heading
       survive intact — and because only text nodes are touched, the heading
       reads identically to a crawler and to a screen reader. */
    function splitWords(el) {
      if (el.dataset.split === 'done') return;
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var nodes = [];
      var node;
      while ((node = walker.nextNode())) nodes.push(node);

      nodes.forEach(function (textNode) {
        var text = textNode.nodeValue;
        if (!text.trim()) return;
        var frag = document.createDocumentFragment();
        // Keep the separators: splitting on a captured group means the spaces
        // come back as their own entries and line breaking is unchanged.
        text.split(/(\s+)/).forEach(function (chunk) {
          if (!chunk) return;
          if (!chunk.trim()) { frag.appendChild(document.createTextNode(chunk)); return; }
          var box = document.createElement('span');
          box.className = 'lr-w';
          var inner = document.createElement('span');
          inner.textContent = chunk;
          box.appendChild(inner);
          frag.appendChild(box);
        });
        textNode.parentNode.replaceChild(frag, textNode);
      });

      $$('.lr-w > span', el).forEach(function (inner, i) {
        inner.style.transitionDelay = (i * 42) + 'ms';
      });
      el.dataset.split = 'done';
      el.classList.add('line-reveal');
    }

    if ('IntersectionObserver' in window) {
      var lineIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          lineIo.unobserve(entry.target);
        });
      }, { threshold: 0.2 });

      $$('[data-line-reveal]').forEach(function (el) {
        splitWords(el);
        lineIo.observe(el);
      });
    }

    /* --- Subtle parallax on decorative layers only ----------------------
       Backgrounds and ornaments, never text or controls. A single scroll
       listener drives every layer off one rAF. */
    var layers = $$('[data-parallax]');
    if (layers.length) {
      var ticking = false;
      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var vh = window.innerHeight;
          layers.forEach(function (layer) {
            var r = layer.getBoundingClientRect();
            if (r.bottom < -200 || r.top > vh + 200) return;
            var depth = parseFloat(layer.getAttribute('data-parallax')) || 8;
            var progress = (r.top + r.height / 2 - vh / 2) / vh;
            layer.style.transform = 'translate3d(0,' + (progress * depth * -1).toFixed(2) + '%,0)';
          });
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* --- Grid-aware stagger for revealed grids ----------------------------
     theme.js already reveals [data-reveal]; this only refines the delay so a
     grid ripples diagonally instead of every card firing at once. */
  $$('[data-reveal-grid]').forEach(function (grid) {
    $$('[data-reveal]', grid).forEach(function (el, i) {
      el.style.setProperty('--reveal-delay', Math.min(i, 8) * 55 + 'ms');
    });
  });
})();
